import { reactive } from 'vue';
import axios from 'axios';
import useCheckout from '@/stores/Checkout';
import useProduct from '@/stores/Product';
import useTransport from '@/stores/Transport';
import useBank from '@/stores/Bank';
import useBonusCode from '@/stores/BonusCode';

const httpInstance = axios.create();

const state = reactive({
  settings: {},
  globalMessage: '',
  errorMessage: '',
  expiresAt: null
});

export default function useBasket() {
  const initBasket = async () => {
    try {
      const {
        data: { items, transport, clientData, basket, success, message }
      } = await httpInstance.get('get/api/shopBasket/basket');

      if (!success && message) {
        state.globalMessage = message;
      }

      if (basket) {
        state.settings = basket;
        if (basket.bonusCode) {
          const { setBonusCode } = useBonusCode();
          setBonusCode(basket.bonusCode);
        }

        if (basket.expiresAt) {
          state.expiresAt = basket.expiresAt * 1000;
        }
      }

      if (items) {
        const { setProducts } = useProduct();
        setProducts(items);
      }

      if (transport) {
        const { setTransport } = useTransport();
        setTransport(transport);
      }

      if (clientData) {
        const { fillUserData } = useCheckout();
        fillUserData(clientData);
      }
    } catch (error) {
      console.error(error);
    }

    return {
      success: true
    };
  };

  const initCheckout = async () => {
    const { loadBanks } = useBank();
    await loadBanks();
  };

  const getSettings = key => {
    return state.settings[key] || null;
  };

  const getGlobalMessage = () => {
    return state.globalMessage;
  };

  const isBasketEmpty = () => {
    const { getProducts } = useProduct();
    return !getProducts().length;
  };

  const getTotalPrice = () => {
    const { getTotalPrice: productTotalPrice } = useProduct();
    const { getTotalPrice: transportTotalPrice } = useTransport();

    return productTotalPrice() + transportTotalPrice();
  };

  const getTotalPriceWithVat = () => {
    const { getTotalPriceWithVat: productTotalPriceWithVat } = useProduct();
    const { getTotalPriceWithVat: transportTotalPriceWithVat } = useTransport();

    return productTotalPriceWithVat() + transportTotalPriceWithVat();
  };

  const getTotalVat = () => {
    return getTotalPriceWithVat() - getTotalPrice();
  };

  const getExpiresAt = () => {
    return state.expiresAt;
  };

  const setExpiresAt = time => {
    state.expiresAt = time;
  };

  const checkInventory = async () => {
    try {
      const {
        data: { success, message }
      } = await httpInstance.get('get/api/shopBasket/checkInventory');

      if (!success && message) {
        state.errorMessage = message;
      }

      return {
        success
      };
    } catch (error) {
      console.error(error);
    }

    return {
      success: false
    };
  };

  const getErrorMessage = () => {
    return state.errorMessage;
  };

  const isAllowInvoicePayment = () => {
    return !!state.settings.allowInvoicePayment;
  };

  return {
    initBasket,
    initCheckout,
    getSettings,
    getGlobalMessage,
    isBasketEmpty,
    getTotalPrice,
    getTotalPriceWithVat,
    getTotalVat,
    getExpiresAt,
    setExpiresAt,
    checkInventory,
    getErrorMessage,
    isAllowInvoicePayment
  };
}
